import type {
  CoachingArtifactDto,
  CoachingCohortDto,
  CoachingCompanyDto,
  CoachingHistoryEventDto,
  CoachingResourceDto,
  CoachingSessionDto
} from "../services/coaching-dtos";
import type { CoachingRole } from "./roles";

export type DashboardReadState = "ready" | "empty" | "error" | "stale-blocked";

export interface DashboardReadModelMetric {
  id: string;
  label: string;
  value: string;
  meta: string;
}

export interface DashboardReadModelRow {
  id: string;
  label: string;
  sourceFamily: DashboardReadSourceFamily;
  scope: string;
  status: string;
}

export type DashboardReadSourceFamily =
  | "company-service"
  | "cohort-service"
  | "session-service"
  | "artifact-service"
  | "resource-service"
  | "history-service";
export interface DashboardReadTotals {
  companies: number | undefined;
  cohorts: number | undefined;
  sessions: number | undefined;
  reports: number | undefined;
  resources: number | undefined;
  history: number | undefined;
}

export interface DashboardReadModelInput {
  role: CoachingRole;
  companies: CoachingCompanyDto[];
  cohorts: CoachingCohortDto[];
  sessions: CoachingSessionDto[];
  reports: CoachingArtifactDto[];
  resources: CoachingResourceDto[];
  history: CoachingHistoryEventDto[];
  totals: DashboardReadTotals;
  readState?: DashboardReadState;
  supportRef?: string;
  previousRows?: DashboardReadModelRow[];
  /** Families the live service explicitly denied (403) for this role scope. */
  deniedFamilies?: DashboardReadSourceFamily[];
}

export interface DashboardReadModel {
  contractKind: "approved-composed-read";
  role: CoachingRole;
  state: DashboardReadState;
  sourceFamilies: DashboardReadSourceFamily[];
  scopePolicy: string;
  metrics: DashboardReadModelMetric[];
  rows: DashboardReadModelRow[];
  totals: DashboardReadTotals;
  supportRef?: string;
  staleDataRendered: boolean;
  deniedFamilies: DashboardReadSourceFamily[];
}

const sourceFamilies: DashboardReadSourceFamily[] = [
  "company-service",
  "cohort-service",
  "session-service",
  "artifact-service",
  "resource-service",
  "history-service"
];

export function composeDashboardReadModel(input: DashboardReadModelInput): DashboardReadModel {
  const state = resolveState(input);

  if (state === "error" || state === "stale-blocked") {
    return {
      contractKind: "approved-composed-read",
      role: input.role,
      state,
      sourceFamilies,
      scopePolicy: getDashboardScopePolicy(input.role),
      metrics: stateMetrics(state, input.supportRef),
      rows: [],
      totals: input.totals,
      supportRef: input.supportRef,
      staleDataRendered: false,
      deniedFamilies: input.deniedFamilies ?? []
    };
  }

  const rows = buildRows(input);

  return {
    contractKind: "approved-composed-read",
    role: input.role,
    state,
    sourceFamilies,
    scopePolicy: getDashboardScopePolicy(input.role),
    metrics: buildMetrics(input),
    rows,
    totals: input.totals,
    supportRef: input.supportRef,
    staleDataRendered: false,
    deniedFamilies: input.deniedFamilies ?? []
  };
}

export function getDashboardScopePolicy(role: CoachingRole) {
  if (role === "super_admin") return "global operations plus backend-gated permission readiness; no secrets or credentials";
  if (role === "admin") return "global operational records; normal admin permission escalation remains absent";
  if (role === "coach") return "service-pre-scoped assigned companies, cohorts, sessions, reports, resources, and history only";
  if (role === "company_admin") return "exactly one service-pre-scoped assigned company with read-only status records";
  return "participant own cohorts, own sessions, own reports, global resources, and no operational history";
}

function resolveState(input: DashboardReadModelInput): DashboardReadState {
  if (input.readState === "error") return "error";
  if (input.readState === "stale-blocked") return "stale-blocked";
  if (
    input.totals.companies === 0 &&
    input.totals.cohorts === 0 &&
    input.totals.sessions === 0 &&
    input.totals.reports === 0 &&
    input.totals.resources === 0 &&
    input.totals.history === 0
  ) {
    return "empty";
  }
  return "ready";
}
function buildMetrics(input: DashboardReadModelInput): DashboardReadModelMetric[] {
  const { totals } = input;
  if (input.role === "participant") {
    return [
      metric("cohorts", "Own cohorts", totals.cohorts, "service-scoped learner paths"),
      metric("sessions", "Upcoming meetings", totals.sessions, "own schedule"),
      metric("reports", "Reports", totals.reports, "own report scope"),
      metric("resources", "Resources", totals.resources, "read-only library")
    ];
  }

  if (input.role === "company_admin") {
    return [
      metric("companies", "Assigned company", totals.companies, "exact-one-company contract"),
      metric("cohorts", "Active cohorts", totals.cohorts, "assigned company"),
      metric("sessions", "Session status", totals.sessions, "read-only"),
      metric("reports", "Reports", totals.reports, "assigned company")
    ];
  }

  return [
    metric("companies", "Companies", totals.companies, input.role === "coach" ? "assigned scope" : "global scope"),
    metric("cohorts", "Cohorts", totals.cohorts, input.role === "coach" ? "assigned work" : "visible operations"),
    metric("sessions", "Sessions", totals.sessions, "schedule scope"),
    metric("reports", "Reports", totals.reports, "artifact scope")
  ];
}

function buildRows(input: DashboardReadModelInput): DashboardReadModelRow[] {
  return [
    ...input.companies.map((company) =>
      row(company.id, company.name, "company-service", getDashboardScopePolicy(input.role), company.status ?? "visible")
    ),
    ...input.cohorts.map((cohort) =>
      row(cohort.id, cohort.name, "cohort-service", getDashboardScopePolicy(input.role), cohort.status ?? "visible")
    ),
    ...input.sessions.map((session) =>
      row(session.id, session.title ?? session.id, "session-service", getDashboardScopePolicy(input.role), session.status ?? "visible")
    ),
    ...input.reports.map((report) =>
      row(report.id, report.title ?? report.id, "artifact-service", getDashboardScopePolicy(input.role), report.status ?? "visible")
    ),
    ...input.resources.map((resource) =>
      row(resource.id, resource.title, "resource-service", getDashboardScopePolicy(input.role), resource.status ?? "visible")
    ),
    ...input.history.map((event) =>
      row(event.id, event.summary ?? event.action ?? event.id, "history-service", getDashboardScopePolicy(input.role), "redacted")
    )
  ];
}

function stateMetrics(state: DashboardReadState, supportRef?: string): DashboardReadModelMetric[] {
  return [
    {
      id: state,
      label: state === "error" ? "Read model error" : "Stale data blocked",
      value: "—",
      meta: supportRef ? `support ref ${supportRef}` : "no previous rows rendered"
    }
  ];
}

function metric(id: string, label: string, value: number | undefined, meta: string): DashboardReadModelMetric {
  return { id, label, value: value === undefined ? "—" : String(value), meta };
}

function row(
  id: string,
  label: string,
  sourceFamily: DashboardReadSourceFamily,
  scope: string,
  status: string
): DashboardReadModelRow {
  return { id, label, sourceFamily, scope, status };
}
